/**
 * PAStatusTimeline — UPS-style vertical step tracker for PA lifecycle.
 * Patient portal version (inline styles, simplified view).
 */

import React, { useState } from "react";

interface TimelineEvent {
  event_id?: string;
  stage?: string;
  stage_label?: string;
  created_at?: string;
  notes?: string;
  [key: string]: unknown;
}

interface Timeline {
  events?: TimelineEvent[];
  days_elapsed?: number;
  days_in_current_stage?: number;
  days_until_renewal?: number;
  progress_percent?: number;
  current_stage?: string;
  [key: string]: unknown;
}

interface PAStatusTimelineProps {
  timeline?: Timeline | null;
  expanded?: boolean;
}

const STAGE_LABELS: Record<string, string> = {
  form_assigned: "Form Assigned",
  physician_completed: "Physician Signed",
  patient_completed: "Patient Signed",
  submitted_to_insurer: "Submitted to Insurer",
  fax_confirmed: "Fax Confirmed",
  under_review: "Under Review",
  requires_info: "Info Requested",
  approved: "Approved",
  denied: "Denied",
  // Your doctor decided not to appeal or resubmit this one. The wording is
  // deliberately the patient's next step, not the insurer's verdict again.
  denial_accepted: "Not Covered — Your Doctor Will Follow Up",
  pharmacy_notified: "Pharmacy Notified",
  pharmacy_received: "Pharmacy Received",
  coverage_verified: "Coverage Verified",
  dispensing: "Preparing Medication",
  ready_for_pickup: "Ready for Pickup",
  completed: "Medication Dispensed",
  renewal_reminder: "Renewal Reminder",
};

function formatDate(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

export default function PAStatusTimeline({ timeline, expanded: defaultExpanded = false }: PAStatusTimelineProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!timeline) return null;

  const { events = [], days_elapsed, days_in_current_stage, days_until_renewal,
    progress_percent = 0, current_stage } = timeline;

  const isDenied = current_stage === "denied";
  const isComplete = current_stage === "completed";

  return (
    <div style={{
      backgroundColor: "white", borderRadius: 12, overflow: "hidden",
      boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: 12,
    }}>
      {/* Summary bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px", background: "white", border: "none", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Status dot */}
          <div style={{
            width: 12, height: 12, borderRadius: "50%",
            backgroundColor: isDenied ? "#c25450" : isComplete ? "#6b9c8e" : "#1b5e6e",
            boxShadow: (!isDenied && !isComplete) ? "0 0 0 3px rgba(59,130,246,0.3)" : "none",
          }} />
          <span style={{ fontSize: 15, fontWeight: 600, color: isDenied ? "#c25450" : "#182a33" }}>
            {STAGE_LABELS[current_stage as string] || current_stage || "No Status"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {/* Days badges */}
          <div style={{ display: "flex", gap: 6 }}>
            {days_elapsed != null && (
              <span style={{
                padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 500,
                backgroundColor: "#f3f4f6", color: "#6b7280",
              }}>
                {days_elapsed}d total
              </span>
            )}
            {days_until_renewal != null && (
              <span style={{
                padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 500,
                backgroundColor: days_until_renewal <= 30 ? "#fae4ca" : "#eef4f2",
                color: days_until_renewal <= 30 ? "#844e1b" : "#46685d",
              }}>
                {days_until_renewal}d to renewal
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div style={{
            width: 60, height: 6, backgroundColor: "#e5e7eb", borderRadius: 3, overflow: "hidden",
          }}>
            <div style={{
              width: `${isDenied ? 100 : progress_percent}%`, height: "100%",
              backgroundColor: isDenied ? "#c25450" : "#1b5e6e", borderRadius: 3,
            }} />
          </div>

          <span style={{ fontSize: 16, color: "#9ca3af" }}>{expanded ? "\u25B2" : "\u25BC"}</span>
        </div>
      </button>

      {/* Expanded timeline */}
      {expanded && (
        <div style={{ padding: "0 20px 16px", backgroundColor: "#fafafa", borderTop: "1px solid #f3f4f6" }}>
          <div style={{ paddingLeft: 20, paddingTop: 12 }}>
            {events.map((ev, i) => {
              const isLast = i === events.length - 1;
              const isDeniedEvent = ev.stage === "denied";
              return (
                <div key={ev.event_id || i} style={{ position: "relative", paddingBottom: isLast ? 0 : 16 }}>
                  {/* Connector */}
                  {!isLast && (
                    <div style={{
                      position: "absolute", left: -14, top: 18, width: 2,
                      height: "calc(100% - 8px)", backgroundColor: "#d1d5db",
                    }} />
                  )}
                  {/* Dot */}
                  <div style={{
                    position: "absolute", left: -18, top: 4,
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: isDeniedEvent ? "#c25450" : isLast ? "#1b5e6e" : "#6b9c8e",
                  }} />
                  {/* Content */}
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        fontSize: 13, fontWeight: 600,
                        color: isDeniedEvent ? "#c25450" : "#1f2937",
                      }}>
                        {ev.stage_label || STAGE_LABELS[ev.stage as string] || ev.stage}
                      </span>
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>{formatDate(ev.created_at)}</span>
                    </div>
                    {ev.notes && (
                      <p style={{
                        fontSize: 12, marginTop: 2, color: isDeniedEvent ? "#c25450" : "#6b7280",
                      }}>
                        {ev.notes}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
